import { useSiteContext, useTranslation } from '@eti/providers';
import { Inline, Text } from '@etg/wings';
import { Circle } from '@phosphor-icons/react';
import { css } from '@eti/styles';
import { duration as durationUtil } from '@eti/utils';
import { screenReadersOnlyStyles } from '../../../../../../utils/styleUtils';
import { getDurationLabel } from '../../../../../../common/time/utils/duration-utils';

const tripInfoStyles = css`
  gap: 0 4px;
  justify-content: center;
`;

const eventPaddingStyles = css`
  padding-inline-start: 4px;
`;

interface TripInfoProps {
  boundIndex: number;
  duration: number;
  numberOfStops: number;
  numberOfTechnicalStops: number;
}

const TripInfo = ({
  boundIndex,
  duration,
  numberOfStops,
  numberOfTechnicalStops,
}: TripInfoProps) => {
  const { language, timeFormat } = useSiteContext();
  const { t } = useTranslation();

  const durationToDisplay = durationUtil.getDuration(duration, language.code, timeFormat);
  const durationLabel = getDurationLabel(language.locale, duration ?? 0);

  const stopText =
    numberOfStops === 1
      ? t('TripDetails.Details.Stop')
      : t('TripDetails.Details.Stops', numberOfStops);

  const technicalStopText =
    numberOfTechnicalStops === 1
      ? t('TripDetails.Details.TechnicalStop.Label.Singular', 1)
      : t('TripDetails.Details.TechnicalStop.Label.Plural', numberOfTechnicalStops);

  return (
    <Inline alignY="center" className={tripInfoStyles}>
      <span className={screenReadersOnlyStyles} id={`bound-${boundIndex}-duration`}>
        {t('ScreenReader.ItineraryButton.Duration.Text', durationLabel)}
      </span>
      <Text>{durationToDisplay}</Text>
      {numberOfStops !== 0 && (
        <div>
          <Circle color="#737373" size={8} weight="fill" />
          <Text className={eventPaddingStyles} id={`bound-${boundIndex}-stops`}>
            {stopText}
          </Text>
        </div>
      )}
      {numberOfTechnicalStops !== 0 && (
        <div>
          <Circle color="#737373" size={8} weight="fill" />
          <Text className={eventPaddingStyles} id={`bound-${boundIndex}-technical-stops`}>
            {technicalStopText}
          </Text>
        </div>
      )}
    </Inline>
  );
};

export default TripInfo;
